import { SignInButton, UserButton, SignedIn, SignedOut } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';

/**
 * Clerk post-sign-in URL for the floating Sign in button.
 * Must route through /auth/callback so complete-signup runs and JWT/session match our API.
 * Using window.location.href here caused returns to e.g. /customer?__clerk_db_jwt=… where
 * SignedOut flashed before hydration; users saw "sign in" while already authenticated.
 */
function clerkSignInForceRedirectUrl() {
  const { pathname, origin } = window.location;
  const test = pathname.startsWith('/__test__/');
  const base = test ? `${origin}/__test__` : origin;
  const cb = (type) => `${base}/auth/callback?type=${type}`;

  if (pathname === '/customer' || pathname === '/__test__/customer') return cb('customer');
  if (pathname === '/' || pathname === '/__test__/') return cb('owner');
  if (pathname.startsWith('/owner/') || pathname.startsWith('/__test__/owner/')) return cb('owner');
  // Menu pages use their own SignInButton with businessId in callbackUrl; keep deep return for AuthCorner here.
  if (/^(\/__test__)?\/m\/[^/]+/.test(pathname)) return window.location.href;

  return window.location.href;
}

/**
 * Fixed sign in / sign out button in the upper right corner.
 * Shown on all pages except transient auth callback.
 */
export default function AuthCorner() {
  const location = useLocation();
  const isAuthCallback = location.pathname === '/auth/callback' || location.pathname.startsWith('/__test__/auth/callback');
  if (isAuthCallback) return null;

  const forceRedirectUrl = clerkSignInForceRedirectUrl();
  const isOwnerPath = location.pathname.startsWith('/owner/') || location.pathname.startsWith('/__test__/owner/');

  return (
    <div className="fixed top-4 right-4 z-50 flex items-center justify-end" aria-label="Account">
      <SignedOut>
        <SignInButton mode="redirect" forceRedirectUrl={forceRedirectUrl}>
          <button className="px-4 py-2 rounded-lg bg-white/95 dark:bg-slate-800/95 text-slate-800 dark:text-slate-100 hover:bg-white dark:hover:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-600 text-sm font-medium transition-colors">
            Sign in
          </button>
        </SignInButton>
      </SignedOut>
      <SignedIn>
        <UserButton
          afterSignOutUrl={isOwnerPath ? '/' : '/customer'}
          appearance={{
            elements: {
              avatarBox: 'w-9 h-9 ring-2 ring-white/80 dark:ring-slate-700 shadow-md',
            },
          }}
        />
      </SignedIn>
    </div>
  );
}
